import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Segmented, Select } from "antd";
import { Plug } from "lucide-react";
import { AN_COLORS, AN_MONO } from "../../../../design-system/tokens";
import { listJumpServer, myTargets } from "../../../../service/sshProxyService";
import { CodeBlock, FieldLabel } from "../../../../pages/enduser/ssh-proxy/sshProxyUi";
import WebTerminalDrawer from "./WebTerminalDrawer";

// Connect to one endpoint as one of the accounts a policy gives the signed-in
// person there (POST /ssh/myTargets): copy the ssh command, or open a browser
// terminal. The command comes from the server — ssh splits on the last "@",
// so it is never assembled here.

const MODES = [
  { value: "command", label: "Connection string" },
  { value: "terminal", label: "Open terminal" },
];

const forEndpoint = (targets, instanceId) =>
  (targets || []).filter((t) => String(t.endpointId) === String(instanceId));

// Gateway id → host-key fingerprint, read once per page load: operators check
// it the first time their ssh client meets the gateway.
let fingerprints = null;
function loadFingerprints() {
  if (!fingerprints) {
    fingerprints = new Promise((resolve) =>
      listJumpServer({
        payload: { pageSize: 200 },
        onSuccess: (data) => {
          const map = {};
          (data?.jumpServers || []).forEach((g) => {
            if (g?.hostKeyFingerprint) map[String(g.id)] = g.hostKeyFingerprint;
          });
          resolve(map);
        },
        onFailure: () => {
          fingerprints = null; // try again next time
          resolve({});
        },
      }),
    );
  }
  return fingerprints;
}

export default function ConnectDialog({ open, instanceId, hostname, targets, onClose }) {
  const mine = useMemo(() => forEndpoint(targets, instanceId), [targets, instanceId]);
  const [account, setAccount] = useState(undefined);
  const [mode, setMode] = useState("command");
  // While the terminal is open the dialog is hidden, not closed: onClose runs
  // once, when the terminal closes.
  const [terminal, setTerminal] = useState(null);

  // Reset when the dialog opens (or moves to another endpoint) — not on every
  // new `targets` array a parent re-render passes in.
  const firstAccount = mine[0]?.account;
  useEffect(() => {
    if (open) {
      setAccount(firstAccount);
      setMode("command");
      setTerminal(null);
    }
  }, [open, instanceId, firstAccount]);

  const target = mine.find((t) => t.account === account) || mine[0];

  const [fingerprint, setFingerprint] = useState("");
  const gatewayId = target?.gatewayId;
  useEffect(() => {
    if (!open || !gatewayId) return undefined;
    let live = true;
    loadFingerprints().then((map) => live && setFingerprint(map[String(gatewayId)] || ""));
    return () => {
      live = false;
    };
  }, [open, gatewayId]);

  const openTerminal = () => {
    if (!target) return;
    setTerminal({ account: target.account, hostname: target.hostname || hostname });
  };

  return (
    <>
      <Modal
        open={open && !terminal}
        onCancel={onClose}
        title={
          <span>
            Connect to <span style={{ fontFamily: AN_MONO }}>{hostname}</span>
          </span>
        }
        width="min(35rem, calc(100vw - 2rem))"
        destroyOnClose
        footer={
          // The code block carries the only Copy; the footer copies nothing.
          mode === "command"
            ? [
                <Button key="close" onClick={onClose}>
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={onClose}>
                  Cancel
                </Button>,
                <Button key="open" type="primary" disabled={!target} onClick={openTerminal}>
                  Open terminal
                </Button>,
              ]
        }
      >
        {/* minmax(0, 1fr): a long command scrolls inside its box instead of
            widening the dialog on narrow screens. */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16, paddingTop: 4 }}>
          <div style={{ minWidth: 0 }}>
            <FieldLabel>Account</FieldLabel>
            <Select
              value={target?.account}
              onChange={setAccount}
              options={mine.map((t) => ({
                value: t.account,
                label: <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{t.account}</span>,
              }))}
              style={{ width: "100%" }}
            />
          </div>
          <Segmented block value={mode} onChange={setMode} options={MODES} />
          {mode === "command" && (
            <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <CodeBlock copyText={target?.command || ""}>{target?.command || "—"}</CodeBlock>
              {fingerprint && (
                <div style={{ marginTop: 8, fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.5 }}>
                  First time? Check that your ssh client shows the fingerprint{" "}
                  <span style={{ fontFamily: AN_MONO, color: AN_COLORS.ink3 }}>{fingerprint}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <WebTerminalDrawer
        open={!!terminal}
        instanceId={instanceId}
        account={terminal?.account}
        hostname={terminal?.hostname}
        onClose={() => {
          setTerminal(null);
          onClose && onClose();
        }}
      />
    </>
  );
}

// The Connect button and its dialog. Given `targets` (the page read myTargets
// once), it uses them; without, it reads them itself. Renders nothing when the
// signed-in person has no account on this endpoint.
export function ConnectButton({ instanceId, hostname, targets }) {
  const [loaded, setLoaded] = useState(null);
  const [open, setOpen] = useState(false);
  const given = targets !== undefined;

  useEffect(() => {
    if (given) return undefined;
    let live = true;
    myTargets({
      onSuccess: (res) => live && setLoaded(res.targets || []),
      onFailure: () => live && setLoaded([]),
    });
    return () => {
      live = false;
    };
  }, [given, instanceId]);

  const all = given ? targets : loaded;
  const mine = useMemo(() => forEndpoint(all, instanceId), [all, instanceId]);
  if (!mine.length) return null;

  return (
    <>
      <Button
        size="small"
        icon={<Plug size={13} />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{ color: AN_COLORS.primary }}
      >
        Connect
      </Button>
      <ConnectDialog open={open} instanceId={instanceId} hostname={hostname} targets={mine} onClose={() => setOpen(false)} />
    </>
  );
}
